import { useState, useEffect, useMemo, memo } from "react";
import { X } from "lucide-react";
import { useLogout, useConfirmEmail } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/ui/avatar";
import { AuthDialog } from "../AuthDialog";
import { AccountSheet } from "../AccountSheet";
import ChatLogo from "./ChatLogo";
import { ChatHeaderProps } from '@/types/componentsTypes';

/** Read reset-password params from the URL (one-shot). */
function useResetTokenFromUrl() {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("resetToken");
    const email = params.get("email");
    if (token) {
      // Clean URL so a page refresh doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete("resetToken");
      url.searchParams.delete("email");
      window.history.replaceState({}, "", url.toString());
      return { token, email: email ?? "" };
    }
    return null;
  }, []);
}

/** Read confirm-email token from URL and auto-confirm (one-shot). */
function useConfirmTokenFromUrl() {
  const confirmMutation = useConfirmEmail();
  const [status, setStatus] = useState<"idle" | "confirming" | "success" | "error">("idle");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("confirmToken");
    if (!token) return;

    // Clean URL immediately
    const url = new URL(window.location.href);
    url.searchParams.delete("confirmToken");
    window.history.replaceState({}, "", url.toString());

    setStatus("confirming");
    confirmMutation.mutate(token, {
      onSuccess: () => setStatus("success"),
      onError: () => setStatus("error"),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return status;
}

const _ChatHeader = ({ onClose }: ChatHeaderProps) => {
  const { t } = useLanguage();
  const { isAuthed, user } = useCurrentUser();
  const logout = useLogout();
  const resetFromUrl = useResetTokenFromUrl();
  const confirmStatus = useConfirmTokenFromUrl();

  const initials =
    ((user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? "")).toUpperCase() || "U";

  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
  if (!isAuthed) setAccountOpen(false);
}, [isAuthed]);

  return (
    <>
      {/* Email confirmation banner */}
      {confirmStatus === "confirming" && (
        <div className="bg-blue-600 text-white text-center text-sm py-2 px-4">
          {t("confirmingEmail")}
        </div>
      )}
      {confirmStatus === "success" && (
        <div className="bg-green-600 text-white text-center text-sm py-2 px-4">
          {t("confirmSuccess")}
        </div>
      )}
      {confirmStatus === "error" && (
        <div className="bg-red-600 text-white text-center text-sm py-2 px-4">
          {t("confirmEmailError")}
        </div>
      )}

    <div className="bg-brand px-4 py-3 flex items-center justify-between">
      <div className="flex items-center">
        <Avatar className="h-10 w-10">
          <ChatLogo />
        </Avatar>
        <div className="ml-3">
          <h2 className="font-semibold text-white">{t("agentName")}</h2>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {isAuthed ? (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex items-center gap-2 outline-none">
                  <span className="inline-flex h-8 w-8 rounded-full overflow-hidden bg-white/10">
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        className="h-full w-full object-cover"
                        alt="avatar"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="m-auto text-xs font-medium text-white">
                        {initials}
                      </span>
                    )}
                  </span>
                  <span className="text-white text-sm underline">{t("manageUser")}</span>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  {t("signedInAs").replace("{email}", user?.emailAddress ?? "")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setAccountOpen(true)}>
                  {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout.mutate()}
                  className="text-red-600 focus:text-red-600"
                >
                  {t("signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <AccountSheet open={accountOpen} onOpenChange={setAccountOpen} />
          </>
        ) : (
          <>
            <AuthDialog defaultForm="register">
              <button className="text-white hover:text-gray-200 transition-colors text-sm underline">
                {t("register")}
              </button>
            </AuthDialog>

            <AuthDialog defaultForm="login">
              <button className="text-white hover:text-gray-200 transition-colors text-sm underline">
                {t("login")}
              </button>
            </AuthDialog>
          </>
        )}

        {/* Auto-opened dialog when the page is loaded with reset-password URL params */}
        {resetFromUrl && (
          <AuthDialog
            defaultForm="reset-password"
            defaultOpen
            resetData={resetFromUrl}
          >
            <span className="hidden" />
          </AuthDialog>
        )}

        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors p-1 rounded"
          title={t("closeChat")}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
    </>
  );
}

// Memoized to prevent rendering on every chat message
export const ChatHeader = memo(_ChatHeader); 